import * as React from 'react'
import { useRef, useEffect, useState } from 'react'
import styled from 'styled-components'
import { loadVGSCollect } from '@vgs/collect-js'
import Button from '@doordash/component-button'
import {
  getVGSVaultEnv,
  getVGSVaultId,
  setFormCname,
  VGSForm,
  VGS_VERSION,
} from '../../../hooks/useVGS'
import { VGS_DOC_UPLOAD_CSS } from './styles'
import { clone, set } from 'lodash'
import {
  CameraFaceTypes,
  ImageCaptureTypes,
} from 'src/components/organisms/workflow-template/types'
import { OnboardingComponentProps } from '.'

export const VGSDocUploadWrapper = styled.div`
  iframe {
    position: absolute;
    width: 100%;
    height: 40px;
    display: ${(props: { loading: boolean }) =>
      props.loading ? 'none' : 'initial'};
  }
`

export const CAMERA_FACE_BACK = 'BACK'
export const CAMERA_FACE_FRONT = 'FRONT'

const TYPE_JPEG = 'jpeg'
const TYPE_JPG = 'jpg'

const submitVGSForm = formRef => {
  return (
    formRef &&
    formRef.current.dispatchEvent(new Event('submit', { cancelable: true }))
  )
}

/**
 * Sets capture preference for file input
 * user: The user-facing camera should be used
 * environment: The outward-facing camera should be used
 * undefined: The user can upload a file from gallery or use camera
 *
 * Currently, camera capture is limited to mobile and is not supported on desktop
 * More info: https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/capture
 */
const getCaptureField = (cameraPreference: CameraFaceTypes) => {
  switch (cameraPreference) {
    case CAMERA_FACE_BACK:
      return ImageCaptureOptions.environment
    case CAMERA_FACE_FRONT:
      return ImageCaptureOptions.user
    default:
      return undefined
  }
}

export enum ImageCaptureOptions {
  environment = 'environment',
  user = 'user',
}

export const VGSDocUploadComponent: React.FC<OnboardingComponentProps> = ({
  templateResponseData,
  onSubmitStep,
  ...props
}) => {
  const formRef = useRef(null)
  const [form, setForm] = useState(null)
  const [docState, setDocState] = useState(null)
  const [formField, setFormField] = useState(null)
  const [vgsCollect, setVgsCollect] = useState(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const capture: ImageCaptureTypes = getCaptureField(props.default_camera_face)

  const { allowed_file_types, id_type, allowed_file_size } = props

  /*
    TODO: Need to update logic here as it will not work if
    the template structure changes or if we have multiple VGS
    components on the page. 
  */
  const id = 'event.actionsList[0].json_data.image_upload_token'

  const onSubmitHandler = e => {
    e.preventDefault()

    if (!docState || docState.files.length === 0) {
      return
    }

    const INPUT_TYPE = id_type
    const data = docState.files[0]
    const fileType = data.type
      .split('/')
      .pop()
      .replace(TYPE_JPEG, TYPE_JPG)
    /*
      Hardcoded for now but should be replaced 
      with timestamp logic once VGS updates
    */
    const uploadMethodType = 'api-file'
    const fileTypePath = `${INPUT_TYPE}.json_data.file_type`
    const uploadMethodTypePath = `${INPUT_TYPE}.json_data.upload_method`
    const imageUploadTokenPath = `${INPUT_TYPE}.json_data.image_upload_token`
    let clonedTemplateResponseData = clone(templateResponseData)
    clonedTemplateResponseData = set(
      clonedTemplateResponseData,
      fileTypePath,
      fileType
    )
    clonedTemplateResponseData = set(
      clonedTemplateResponseData,
      uploadMethodTypePath,
      uploadMethodType
    )
    clonedTemplateResponseData = set(
      clonedTemplateResponseData,
      imageUploadTokenPath,
      ''
    )
    onSubmitStep(clonedTemplateResponseData, { form })
    setIsLoading(true)
  }

  useEffect(() => {
    const loadForm = async () => {
      const vgs_collect = await loadVGSCollect({
        vaultId: getVGSVaultId(),
        environment: getVGSVaultEnv(),
        version: VGS_VERSION,
      }).catch(e => {
        console.log(e)
      })
      setVgsCollect(vgs_collect)
    }

    if (!form) loadForm()

    return () => {
      if (formField) formField.delete()
    }
  }, [form, formField])

  useEffect(() => {
    let uploaded = false
    const stateHandler = (state, formRef) => {
      if (
        state[id] &&
        state[id].isValid === true &&
        state[id].files.length > 0 &&
        !uploaded
      ) {
        uploaded = true
        setDocState(state[id])
        submitVGSForm(formRef)
      }
    }
    const initForm = vgs_collect => {
      const form: VGSForm = vgs_collect.init(state =>
        stateHandler(state, formRef)
      )
      setFormCname(form)
      setFormField(
        form.field('#file-upload', {
          type: 'file', // required
          name: id, // required
          validations: ['required'],
          multiple: false,
          serializers: [{ name: 'toBase64' }],
          accept: allowed_file_types,
          capture,
          maxFileSize: +allowed_file_size,
          css: VGS_DOC_UPLOAD_CSS,
        })
      )
      setForm(form)
      setIsLoading(false)
    }

    if (vgsCollect) initForm(vgsCollect)
  }, [vgsCollect, id, capture, allowed_file_size, allowed_file_types])

  return (
    <VGSDocUploadWrapper loading={isLoading}>
      <form ref={formRef} id="vgs-form" onSubmit={onSubmitHandler}>
        <Button
          id="file-upload"
          state={isLoading ? Button.States.Loading : Button.States.Default}
        >
          {props.label}
        </Button>
      </form>
    </VGSDocUploadWrapper>
  )
}
